/**
 * ProfileEditForm — inline edit affordance for the Profile section on
 * `/users/:userId`. Tier 0 (slice-local) per ADR `frontend-0004`.
 *
 * Composes the branded primitives from `@artemis/validators`
 * (`phoneSchema`, `IanaTimeZoneSchema`, `languageCodeSchema`,
 * `countryCodeSchema`) into a form-local Zod schema. This deliberately
 * does NOT lift the schema into `@artemis/validators` because the BE
 * already owns `updateUserProfileSchema` in its DTO module; consolidation
 * is a separate concern that requires the BE to consume the shared
 * schema, which is out of Phase 2 scope. The primitives ARE shared —
 * the composition is local.
 *
 * Submit semantics: every known field is sent on save. Empty-string
 * for a nullable text field is normalised to `null` so the BE's
 * "null clears" semantics fire instead of round-tripping an empty
 * string. Validation defers to RHF's `onSubmit` mode so the user can
 * type freely without per-keystroke red borders.
 */

import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  phoneSchema,
  IanaTimeZoneSchema,
  languageCodeSchema,
  countryCodeSchema,
} from '@artemis/validators';
import { Button } from '@sparta/ui-kit';
import type { OrgMemberDetailView } from '../api/types.js';

const formSchema = z.object({
  firstName: z.string().max(100),
  lastName: z.string().max(100),
  jobTitle: z.string(),
  department: z.string(),
  phone: z.string(),
  timeZone: z.string(),
  language: z.string(),
  country: z.string(),
  managerId: z.string(),
});

type FormValues = z.infer<typeof formSchema>;

interface SubmitBody {
  firstName: string | null;
  lastName: string | null;
  jobTitle: string | null;
  department: string | null;
  phone: string | null;
  timeZone: string;
  language: string;
  country: string | null;
  managerId: string | null;
}

export interface ProfileEditFormProps {
  readonly initial: OrgMemberDetailView;
  readonly onSubmit: (body: SubmitBody) => void | Promise<void>;
  readonly onCancel: () => void;
  /** External error banner — e.g. "412 stale" message from the caller. */
  readonly error?: string;
  /** Disable Save while the caller's mutation is in flight. */
  readonly isPending?: boolean;
}

function normaliseNullable(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function defaultValues(initial: OrgMemberDetailView): FormValues {
  return {
    firstName: initial.firstName ?? '',
    lastName: initial.lastName ?? '',
    jobTitle: initial.jobTitle ?? '',
    department: initial.department ?? '',
    phone: initial.phone ?? '',
    timeZone: initial.timeZone,
    language: initial.language,
    country: initial.country ?? '',
    managerId: initial.managerId ?? '',
  };
}

export function ProfileEditForm({
  initial,
  onSubmit,
  onCancel,
  error,
  isPending = false,
}: ProfileEditFormProps): React.ReactElement {
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    mode: 'onSubmit',
    defaultValues: defaultValues(initial),
    resolver: zodResolver(formSchema),
  });

  // Local field-level error state for the branded primitives — RHF's
  // own error map handles the schema-level (presence/shape) errors; the
  // branded primitives are richer than what `zodResolver` surfaces by
  // default, so we keep a parallel map and merge for display.
  const [brandedErrors, setBrandedErrors] = React.useState<
    Partial<Record<keyof FormValues, string>>
  >({});
  const showError = (field: keyof FormValues): string | undefined =>
    brandedErrors[field] ?? errors[field]?.message;

  const submitHandler = handleSubmit((values) => {
    // Validate the branded primitives at submit time. If any fail,
    // we set the parallel `brandedErrors` map and return without
    // calling `onSubmit` — RHF still considers the submit handled.
    const next: Partial<Record<keyof FormValues, string>> = {};
    const phoneNormalised = normaliseNullable(values.phone);
    if (phoneNormalised !== null) {
      if (!phoneSchema.safeParse(phoneNormalised).success) {
        next.phone =
          'Phone must be in E.164 format (e.g. +12025551234)';
      }
    }
    if (!IanaTimeZoneSchema.safeParse(values.timeZone).success) {
      next.timeZone =
        'Time zone must be a valid IANA identifier (e.g. America/New_York)';
    }
    if (!languageCodeSchema.safeParse(values.language).success) {
      next.language =
        'Language must be a valid BCP-47 code (e.g. en, fr)';
    }
    const countryNormalised = normaliseNullable(values.country);
    if (countryNormalised !== null) {
      if (!countryCodeSchema.safeParse(countryNormalised).success) {
        next.country =
          'Country must be a valid ISO-3166-1 alpha-2 code (e.g. US)';
      }
    }
    if (Object.keys(next).length > 0) {
      setBrandedErrors(next);
      return;
    }

    const body: SubmitBody = {
      firstName: normaliseNullable(values.firstName),
      lastName: normaliseNullable(values.lastName),
      jobTitle: normaliseNullable(values.jobTitle),
      department: normaliseNullable(values.department),
      phone: phoneNormalised,
      timeZone: values.timeZone,
      language: values.language,
      country: countryNormalised,
      managerId: normaliseNullable(values.managerId),
    };
    return onSubmit(body);
  });

  return (
    <form
      onSubmit={(e) => {
        setBrandedErrors({});
        void submitHandler(e);
      }}
      noValidate
      className="flex flex-col gap-4"
      aria-label="Edit profile"
    >
      {error !== undefined ? (
        <div
          role="alert"
          className="rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] p-3 text-sm text-[var(--fg-secondary)]"
        >
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          id="profile-firstName"
          label="First name"
          register={register('firstName')}
          error={showError('firstName')}
        />
        <Field
          id="profile-lastName"
          label="Last name"
          register={register('lastName')}
          error={showError('lastName')}
        />
        <Field
          id="profile-jobTitle"
          label="Job title"
          register={register('jobTitle')}
          error={showError('jobTitle')}
        />
        <Field
          id="profile-department"
          label="Department"
          register={register('department')}
          error={showError('department')}
        />
        <Field
          id="profile-phone"
          label="Phone"
          register={register('phone')}
          error={showError('phone')}
          placeholder="+12025551234"
        />
        <Field
          id="profile-timeZone"
          label="Time zone"
          register={register('timeZone')}
          error={showError('timeZone')}
          placeholder="America/New_York"
        />
        <Field
          id="profile-language"
          label="Language"
          register={register('language')}
          error={showError('language')}
          placeholder="en"
        />
        <Field
          id="profile-country"
          label="Country"
          register={register('country')}
          error={showError('country')}
          placeholder="US"
        />
        <Field
          id="profile-managerId"
          label="Manager (UUID)"
          register={register('managerId')}
          error={showError('managerId')}
          placeholder="UUID of the manager"
        />
      </div>

      <div className="flex justify-end gap-2 border-t border-[var(--border-subtle)] pt-3">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={!isDirty || isPending}
        >
          {isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}

interface FieldProps {
  readonly id: string;
  readonly label: string;
  readonly register: ReturnType<ReturnType<typeof useForm<FormValues>>['register']>;
  readonly error?: string;
  readonly placeholder?: string;
}

function Field({
  id,
  label,
  register,
  error,
  placeholder,
}: FieldProps): React.ReactElement {
  const errorId = `${id}-error`;
  return (
    <label className="flex flex-col gap-1 text-xs text-[var(--fg-tertiary)]">
      <span>{label}</span>
      {/*
       * SCRUM-108 Phase 2: dense inline edit form. The `register`
       * spread provides RHF's onChange/ref binding; the kit's
       * TextField primitive isn't a drop-in (it owns its own
       * label + 44 px floor + rounded-xl chrome that would
       * dominate this dense grid). A future FilterInput Tier-2
       * primitive should cover both this and the Phase 1 search
       * input once a second consumer asks for the pattern. The
       * `@sparta/no-hand-rolled-input` rule does not fire here
       * because the input receives `{...register}` rather than a
       * bare static attributes object.
       */}
      <input
        id={id}
        {...register}
        placeholder={placeholder}
        aria-invalid={error !== undefined}
        aria-describedby={error !== undefined ? errorId : undefined}
        className="rounded-md border border-[var(--border-default)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--fg-primary)]"
      />
      {error !== undefined ? (
        <span id={errorId} role="alert" className="text-xs text-[var(--fg-danger,red)]">
          {error}
        </span>
      ) : null}
    </label>
  );
}
